class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');

    this.updateResultsHeight = () => {
      if (this.hasAttribute('open')) this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    };
    this.setupEventListeners();
  }

  connectedCallback() {
    window.addEventListener('resize', this.updateResultsHeight);
    window.visualViewport?.addEventListener('resize', this.updateResultsHeight);
    window.visualViewport?.addEventListener('scroll', this.updateResultsHeight);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.updateResultsHeight);
    window.visualViewport?.removeEventListener('resize', this.updateResultsHeight);
    window.visualViewport?.removeEventListener('scroll', this.updateResultsHeight);
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  } 

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = ''; 

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
 
    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.searchRequest?.abort();
    this.searchRequest=new AbortController();
    const request=window.wdltStrictSearch.urlFor(searchTerm,routes.search_url);
    request.searchParams.set('section_id','wdlt-predictive-strict');
    fetch(request,{signal:this.searchRequest.signal})
      .then((response) => { 
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if(this.getQuery()!==searchTerm)return;
        const result=new DOMParser().parseFromString(text,'text/html').querySelector('#shopify-section-wdlt-predictive-strict');
        const scoped=request.searchParams.get('q');
        const walker=document.createTreeWalker(result,NodeFilter.SHOW_TEXT);
        while(walker.nextNode())walker.currentNode.textContent=walker.currentNode.textContent.split(scoped).join(searchTerm);
        const resultsMarkup=result.innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if(error.name==='AbortError')return;
        if(this.getQuery()===searchTerm)this.close();
      }); 
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    const brandMapNode = document.getElementById('wdlt-brand-collection-map');
    const term = this.getQuery().trim().toLocaleLowerCase();
    if (brandMapNode && term.length >= 2) {
      let matches = [];
      try {
        const normalize = name => String(name).toLocaleLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
        const map = JSON.parse(brandMapNode.textContent);
        matches = map.filter(entry => normalize(entry.name).split(/[^a-z0-9]+/i).some(word => word.startsWith(normalize(term))))
          .sort((a,b) => (normalize(b.name) === normalize(term)) - (normalize(a.name) === normalize(term)) || a.name.length - b.name.length)
          .slice(0,3);
      } catch (_) {}
      const list = this.predictiveSearchResults.querySelector('#predictive-search-results-list');
      if (list) {
        matches.reverse().forEach(entry => {
          const li = document.createElement('li');
          li.className = 'predictive-search__list-item wdlt-brand-result';
          li.setAttribute('role', 'option');
          li.setAttribute('aria-selected', 'false');
          const a = document.createElement('a');
          a.className = 'wdlt-brand-result__link';
          a.href = entry.url;
          a.textContent = 'Shop All ' + entry.name.toLocaleUpperCase();
          const arrow = document.createElement('span');
          arrow.className = 'wdlt-brand-result__arrow';
          arrow.setAttribute('aria-hidden','true');
          arrow.textContent = '→';
          a.append(arrow); li.append(a); list.prepend(li);
        });
      }
    }
    this.setAttribute('results', true);  

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() { 
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  } 

  getResultsMaxHeight() {
    const viewport = window.visualViewport;
    const visibleBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
    const resultsTop = this.predictiveSearchResults.getBoundingClientRect().top;
    const limit = window.matchMedia('(max-width: 749px)').matches ? 280 : 360;
    return Math.max(0, Math.min(limit, visibleBottom - resultsTop - 12));
  }

  open() {
    this.setAttribute('open', true);
    this.updateResultsHeight();
    this.input.setAttribute('aria-expanded', true);
  }

  close(clearSearchTerm = false) { 
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);


